<!--赖传峰-->
<!--2022/1/20-->
<template>
  <ClientOnly>
    <teleport to="body">
      <div
        v-show="state.visible"
        :style="style"
        class="swipe-select-box"
      />
    </teleport>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useLayout } from '@/store/layout'
import { useEventListener } from '@vueuse/core'
import { computed, reactive } from 'vue'

export interface SwipeSelectBoxState {
  visible: boolean
  left: number
  top: number
  width: number
  height: number
}

const props = defineProps<{ triggerElement: HTMLElement }>()

const emit = defineEmits<{
  (e: 'start', event: MouseEvent): void
  (e: 'swipe', event: MouseEvent, boundingRect: Omit<SwipeSelectBoxState, 'visible'>): void
  (e: 'done', event: MouseEvent): void
}>()

const initialState: SwipeSelectBoxState = {
  visible: false,
  left: 0,
  top: 0,
  width: 0,
  height: 0,
}
const startCoordinate = { x: 0, y: 0 } // 鼠标点击的起始坐标
const state = reactive<SwipeSelectBoxState>({ ...initialState }) // 使用解构断开引用，initialState 不能被影响

const style = computed(() => ({
  left: `${state.left}px`,
  top: `${state.top}px`,
  width: `${state.width}px`,
  height: `${state.height}px`,
}))

const events = {
  down(e: MouseEvent) {
    if (e.button !== 0) return // 只响应用户鼠标的主按键
    Object.assign(state, { visible: true, left: e.x, top: e.y })
    Object.assign(startCoordinate, { x: e.x, y: e.y })
    emit('start', e)
  },
  move(e: MouseEvent) {
    if (e.button !== 0) return // 只响应用户鼠标的主按键
    if (!state.visible) return

    const offsetX = e.x - startCoordinate.x
    const offsetY = e.y - startCoordinate.y

    const newState = {
      left: offsetX < 0 ? e.x : startCoordinate.x,
      top: offsetY < 0 ? e.y : startCoordinate.y,
      width: Math.abs(offsetX),
      height: Math.abs(offsetY),
    }

    Object.assign(state, newState)
    emit('swipe', e, newState)
  },
  up(e: MouseEvent) {
    if (e.button !== 0) return // 只响应用户鼠标的主按键
    Object.assign(state, initialState)
    emit('done', e)
  },
}

if (!useLayout().isMobile) {
  useEventListener(props.triggerElement, 'mousedown', events.down)
  useEventListener('mousemove', events.move)
  useEventListener('mouseup', events.up)
}
</script>

<style lang="less" scoped>
.swipe-select-box {
  position: fixed;
  border: 1px solid #ccc;
  background: fade(#ddd, 30%);
  pointer-events: none; // 点击事件穿透选择框，防止文件的鼠标事件被选择框遮挡
  z-index: 1;
}
</style>
